import { NgForOf, NgIf } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import type { MatChipInputEvent } from '@angular/material/chips';
import { MatChipGrid, MatChipInput, MatChipRemove, MatChipRow } from '@angular/material/chips';
import { EMAIL_EXP } from '@cf/core';
import {
  FormFieldComponent,
  InputDirective,
  InputErrorComponent,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { LinkComponent } from '@cf/shared/ui/button';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';

@Component({
  selector: 'cf-booking-guests',
  templateUrl: './booking-guests.component.html',
  styleUrls: ['./booking-guests.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    LinkComponent,
    NgIf,
    FormFieldComponent,
    MatChipGrid,
    LabelComponent,
    ReactiveFormsModule,
    MatChipRow,
    NgForOf,
    MatChipInput,
    InputErrorComponent,
    InputDirective,
    CfIconDirective,
    MatChipRemove,
    NgxTolgeeModule,
  ],
})
export class BookingGuestsComponent implements OnChanges {
  translateService = inject(TranslateService);

  @Input() email!: string;

  @Input() emails!: string[];

  @Output() emitChange: EventEmitter<string[]> = new EventEmitter();

  emailsControl = new FormControl('', [Validators.pattern(EMAIL_EXP)]);

  errorMsg = signal<string | null>(null);

  showGuest = false;

  ngOnChanges(changes: SimpleChanges) {
    this.showGuest = !!this.emails.length;
    if (changes.email) {
      if (this.emails.includes(this.email)) {
        this.removeEmail(this.email);
      }
    }
  }

  removeEmail(email: string) {
    this.errorMsg.set('');
    const index = this.emails.indexOf(email);

    if (index >= 0) {
      this.emails.splice(index, 1);
      this.emitChange.emit(this.emails);
    }
  }

  addEmail(event: MatChipInputEvent) {
    const value = (event.value || '').trim();

    if (!value) return;

    if (!EMAIL_EXP.test(value)) {
      this.errorMsg.set(this.translateService.instant('validation.email_is_not_valid'));
      return;
    }

    if (this.emails.includes(value)) {
      this.errorMsg.set(this.translateService.instant('validation.email_already_exists'));
      return;
    }

    this.errorMsg.set(null);
    this.emails = [...this.emails, value];
    this.emitChange.emit(this.emails);

    event.chipInput.clear();
  }

  toggleGuest() {
    this.showGuest = true;
  }
}
